import useStealthex from '../hooks/useStealthex'
import { InputError } from '../interfaces/swapInterface'
import { useConvertContext } from '../providers/ConvertProvider'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useBalances from './useBalances'
import { logger } from '../logger'

interface Props {
  fromTokenId?: string
}

const useConvert = ({ fromTokenId }: Props) => {
  const { setFromToken, fromToken, toToken, amount, toAmount, setToAmount } = useConvertContext()

  const { t } = useTranslation()

  const { findBalance } = useBalances()

  const { tokens, range, fetchEstimatedAmount, destinationTokens } = useStealthex({
    from: fromToken,
    to: toToken,
  })

  const error: InputError | undefined = useMemo(() => {
    if (!amount) return { buttonError: t('swap.errors.no_amount') }

    const value = parseFloat(amount)

    if (range) {
      if (value < range.minAmount)
        return {
          buttonError: 'Invalid amount',
          inputFieldError: `Min amount ${range.minAmount}`,
        }

      if (range.maxAmount && value > range.maxAmount) {
        return {
          buttonError: 'Invalid amount',
          inputFieldError: `Max amount ${range.maxAmount}`,
        }
      }
    }

    const balance = findBalance(fromToken)
    if (balance && value > balance.balance) {
      return {
        buttonError: t('swap.errors.not_enough_token', {
          token: fromToken!.symbol,
        }),
      }
    }

    if (!toToken) {
      return {
        buttonError: t('swap.errors.no_token'),
      }
    }

    if (!toAmount) {
      return {
        buttonError: t('swap.errors.loading_price'),
      }
    }
  }, [amount, toAmount, range, fromToken?.id])

  useEffect(() => {
    if (!amount) return

    const timeout = setTimeout(() => {
      fetchEstimatedAmount(parseFloat(amount))
        .then((estimated) => setToAmount(estimated ? estimated.toString() : ''))
        .catch((e) =>
          logger.error('Failed to fetch conversion estimate', {
            originalError: e,
          })
        )
    }, 300)

    return () => clearTimeout(timeout)
  }, [amount, setToAmount, fromToken?.id, toToken?.id])

  useEffect(() => {
    if (fromToken) return

    const fToken = getTokenById(fromTokenId)

    if (fToken) {
      setFromToken(fToken)
    } else {
      const icpToken = tokens.find((token) => token.symbol === 'ICP')
      setFromToken(icpToken)
    }
  }, [tokens, fromTokenId])

  const getTokenById = (id: string | undefined) =>
    id ? tokens.find((token) => token.id === id) : undefined

  return {
    getTokenById,
    tokens,
    destinationTokens,
    error,
  }
}

export default useConvert
